// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import useUploadNotificationsGeneric from '../../../hooks/useUploadNotificationsGeneric';
import TransactionStatusBadge from './TransactionStatusBadge';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import UserLink from '../../common/links/UserLink';
import YesNoLabel from '../../common/YesNoLabel';
import { useTranslation } from 'react-i18next';
import { exportDisabledTransactionData, getDisabledTransactions } from '../../../api/keyCodeApi';

export interface ReportedDisabledListProps {
  reload?: any;
  disabledTransactions?: any;
}

function ReportedDisabledList({ reload, disabledTransactions }: ReportedDisabledListProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const defaultVisibleColumns = [
    'creationDate',
    'batchCount',
    'keyProviderName',
    'manualImportUser',
    'reasons',
    'reason',
    'description',
    'status',
  ];
  const allColumns = [...defaultVisibleColumns, 'isDisabledOnSteam'];
  const [transactions, setTransactions] = useState<any>();
  const params = new URLSearchParams(location.search);

  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'reporteddisabledlist-settings',
    getTableSettingsDefaults({
      defaultSort: 'creationDate',
      direction: 'desc',
      defaultVisibleColumns,
    }),
    null,
    getDisabledTransactions,
    setTransactions,
    reload,
    t('Failed to load disabled transactions')
  );

  useUploadNotificationsGeneric(
    transactions,
    setTransactions,
    'disabled',
    disabledTransactions,
    'id',
    (row: any) => row.status === 'Pending'
  );

  let columnsConfig = [
    {
      Key: 'creationDate',
      Label: t('Requested date'),
      width: '10%',
      Render: (item: any) => (
        <Link to={'/keys/reported-disabled/' + item.id}>
          <FormattedDate value={item.creationDate} variant="default" />
        </Link>
      ),
    },
    {
      Key: 'batchCount',
      Label: t('Key count'),
      className: 'text-right num no-wrap',
      width: '5%',
      Render: (item: any) =>
        item.batchCount >= 0 ? CommaNumber_NoDecimal(item.batchCount) : '--',
    },
    {
      Key: 'keyProviderName',
      Label: t('Provider'),
      width: '10%',
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProviderName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'manualImportUser',
      Label: t('Requested by'),
      width: '10%',
      Render: (item: any) => (
        <>
          <UserLink id={item.manualImportUserId} name={item.manualImportUser} />
          <br />
          {item.manualImportUserCompany}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'status',
      Label: t('Status'),
      width: '5%',
      Render: (item: any) => (
        <TransactionStatusBadge
          success={item.success}
          partialSuccess={item.partialSuccess}
          message={item.message}
          status={item.status}
          isAuthorised={item.isAuthorised}
        ></TransactionStatusBadge>
      ),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, (el.target as HTMLSelectElement).value)}
            >
              <option value="">{t('All')}</option>
              <option value="failed">{t('Failed')}</option>
              <option value="partial success">{t('Partial success')}</option>
              <option value="pending authorisation">{t('Pending authorisation')}</option>
              <option value="pending">{t('Pending')}</option>
              <option value="success">{t('Success')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'reasons',
      Label: t('Reason'),
      width: '10%',
      Render: (item: any) =>
        item.reasons?.map((x: string) => (
          <span key={x} className="badge bg-pink me-2">
            {x}
          </span>
        )),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'reason',
      Label: t('More info'),
      width: '15%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'description',
      Label: t('Description'),
      width: '15%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'isDisabledOnSteam',
      Label: t('Disabled on Steam'),
      width: '15%',
      Render: (item: any) => <YesNoLabel value={item.isDisabledOnSteam} />,
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, (el.target as HTMLSelectElement).value)}
            >
              <option value="">{t('--- All ---')}</option>
              <option value="true">{t('Yes')}</option>
              <option value="false">{t('No')}</option>
            </select>
          );
        },
      },
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return transactions ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={transactions.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={transactions.totalRowCount}
        filteredRowCount={transactions.filteredRowCount}
        scopes={transactions.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportDisabledTransactionData}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default ReportedDisabledList;
